/**
 * The small glyphs the Scores surfaces share (Scores UX spec v1.0): chevrons,
 * a plus, a calendar, a trophy and the inline favorite star. One source, so a
 * "See all" chevron and a Standings chevron can never drift apart. The
 * search glyph opens the Scores search (ISSUE-1380). Mobile's
 * mirror is `apps/mobile/src/ui/iconGlyphs.ts`.
 *
 * Every glyph is decorative (`aria-hidden`): the control around it carries the
 * name. Size is a token class, `iconSm` (16) unless stated.
 */
import { cn } from "@sportsmet/ui";
import type { ReactNode } from "react";

import { STAR_PATH } from "./actionIcons";

type typeGlyphProps = {
  /** `icon-sm` (16px) or `icon-md` (20px). */
  size?: "sm" | "md";
  className?: string;
  testId?: string;
};

function glyphClass(props: typeGlyphProps): string {
  return cn(
    props.size === "md" ? "h-icon-md w-icon-md" : "h-icon-sm w-icon-sm",
    "shrink-0",
    props.className,
  );
}

export function ChevronRightIcon(props: typeGlyphProps): ReactNode {
  return (
    <svg
      className={glyphClass(props)}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      data-icon="chevron-right"
      data-testid={props.testId}
    >
      <path d="m9 5 7 7-7 7" />
    </svg>
  );
}

export function ChevronLeftIcon(props: typeGlyphProps): ReactNode {
  return (
    <svg
      className={glyphClass(props)}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      data-icon="chevron-left"
      data-testid={props.testId}
    >
      <path d="m15 5-7 7 7 7" />
    </svg>
  );
}

export function ChevronDownIcon(props: typeGlyphProps): ReactNode {
  return (
    <svg
      className={glyphClass(props)}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      data-icon="chevron-down"
      data-testid={props.testId}
    >
      <path d="m5 9 7 7 7-7" />
    </svg>
  );
}

export function PlusIcon(props: typeGlyphProps): ReactNode {
  return (
    <svg
      className={glyphClass(props)}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
      data-icon="plus"
      data-testid={props.testId}
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

export function CalendarIcon(props: typeGlyphProps): ReactNode {
  return (
    <svg
      className={glyphClass(props)}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
      data-icon="calendar"
      data-testid={props.testId}
    >
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </svg>
  );
}

export function TrophyIcon(props: typeGlyphProps): ReactNode {
  return (
    <svg
      className={glyphClass(props)}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      data-icon="trophy"
      data-testid={props.testId}
    >
      <path d="M8 4h8v5a4 4 0 0 1-8 0z" />
      <path d="M16 6h3a2 2 0 0 1-3 4M8 6H5a2 2 0 0 0 3 4" />
      <path d="M12 13v4M9 20h6M10 17h4v3h-4z" />
    </svg>
  );
}

/** The filled star after a followed team's name on a score row. Never a
 *  control: following lives on the team page and the game header. */
export function FavoriteStarIcon(props: typeGlyphProps): ReactNode {
  return (
    <svg
      className={glyphClass(props)}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
      data-icon="favorite-star"
      data-testid={props.testId}
    >
      <path d={STAR_PATH} />
    </svg>
  );
}

/** A magnifying glass, for the Scores search entry (ISSUE-1380). */
export function SearchIcon(props: typeGlyphProps): ReactNode {
  return (
    <svg
      className={glyphClass(props)}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      data-icon="search"
      data-testid={props.testId}
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}
